import { useLanguage } from "@/context/language"
import { dateFormatter } from "@/utils/date"
import { getUsageStats } from "@/cowork/api"
import { DialogBody, DialogHeader, DialogTitle, DialogV2 } from "@opencode-ai/ui/v2/dialog-v2"
import { createResource, For, Show, type JSX } from "solid-js"

export function DialogCoworkUsage() {
  const language = useLanguage()
  const [usage] = createResource(getUsageStats)
  const stats = () => usage()
  const cards = () => [
    { label: language.t("cowork.usage.projects"), value: stats()?.projects ?? 0 },
    { label: language.t("cowork.usage.workflows"), value: stats()?.workflows ?? 0 },
    { label: language.t("cowork.usage.chats"), value: stats()?.chats ?? 0 },
    { label: language.t("cowork.usage.files"), value: stats()?.files ?? 0 },
    { label: language.t("cowork.usage.extractions"), value: stats()?.extractions ?? 0 },
    { label: language.t("cowork.usage.transformations"), value: stats()?.transformations ?? 0 },
    { label: language.t("cowork.usage.outputTables"), value: stats()?.output_tables ?? 0 },
    { label: language.t("cowork.usage.reports"), value: stats()?.reports ?? 0 },
    { label: language.t("cowork.usage.approvals"), value: stats()?.approvals ?? 0 },
  ]
  return (
    <DialogV2 size="x-large" variant="settings" class="!overflow-hidden">
      <DialogHeader closeLabel={language.t("common.close")}>
        <DialogTitle>{language.t("cowork.usage.title")}</DialogTitle>
      </DialogHeader>
      <DialogBody class="min-h-0 flex-1 gap-0 overflow-hidden p-0">
        <div class="min-h-0 flex-1 space-y-5 overflow-y-auto p-5 [scrollbar-color:var(--v2-border-border-base)_transparent] [scrollbar-gutter:stable] [scrollbar-width:thin]">
          <div class="rounded-2xl border border-v2-border-border-base bg-v2-background-bg-layer-01 p-5 shadow-[var(--v2-elevation-card)]">
            <div class="flex flex-wrap items-center justify-between gap-2">
              <div>
                <div class="text-11-medium uppercase tracking-[0.18em] text-v2-text-text-muted">{language.t("cowork.usage.modelTitle")}</div>
                <div class="mt-1 text-24-semibold text-v2-text-text-base">{formatNumber(stats()?.total_tokens ?? 0)} {language.t("cowork.usage.totalTokens")}</div>
              </div>
              <div class="text-12-regular text-v2-text-text-muted">
                {language.t("cowork.usage.lastUpdated", { value: formatDate(stats()?.last_updated_at) })}
              </div>
            </div>
            <div class="mt-4 grid gap-3 md:grid-cols-4">
              <UsageCard label={language.t("cowork.usage.inputTokens")} value={formatNumber(stats()?.input_tokens ?? 0)} />
              <UsageCard label={language.t("cowork.usage.outputTokens")} value={formatNumber(stats()?.output_tokens ?? 0)} />
              <UsageCard label={language.t("cowork.usage.totalTokens")} value={formatNumber(stats()?.total_tokens ?? 0)} />
              <UsageCard label={language.t("cowork.usage.estimatedCost")} value={`$${(stats()?.estimated_cost ?? 0).toFixed(2)}`} />
            </div>
          </div>
          <div class="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            <For each={cards()}>{(card) => <UsageCard compact label={card.label} value={formatNumber(card.value)} />}</For>
          </div>
          <div class="grid gap-4 lg:grid-cols-2">
          <UsageSection title={language.t("cowork.usage.efficiencyTitle")}>
            <div class="grid gap-3 sm:grid-cols-2">
              <UsageCard compact label={language.t("cowork.usage.successRate")} value={`${formatNumber(getNumber(stats()?.efficiency, "success_rate"))}%`} />
              <UsageCard compact label={language.t("cowork.usage.failedRuns")} value={formatNumber(getNumber(stats()?.efficiency, "failed_runs"))} />
              <UsageCard compact label={language.t("cowork.usage.avgTokensPerChat")} value={formatNumber(getNumber(stats()?.efficiency, "average_tokens_per_chat"))} />
              <UsageCard compact label={language.t("cowork.usage.avgCostPerChat")} value={`$${getNumber(stats()?.efficiency, "average_cost_per_chat").toFixed(4)}`} />
              <UsageCard compact label={language.t("cowork.usage.avgResponseSeconds")} value={`${formatNumber(getNumber(stats()?.efficiency, "average_response_seconds"))}s`} />
              <UsageCard compact label={language.t("cowork.usage.timeSaved")} value={formatDuration(getNumber(stats()?.efficiency, "estimated_time_saved_minutes"))} />
            </div>
          </UsageSection>
          <UsageSection title={language.t("cowork.usage.fileStatsTitle")}>
            <div class="grid gap-3 sm:grid-cols-2">
              <UsageCard compact label={language.t("cowork.usage.images")} value={formatNumber(getNumber(stats()?.file_stats, "images"))} />
              <UsageCard compact label={language.t("cowork.usage.pdfs")} value={formatNumber(getNumber(stats()?.file_stats, "pdfs"))} />
              <UsageCard compact label={language.t("cowork.usage.spreadsheets")} value={formatNumber(getNumber(stats()?.file_stats, "spreadsheets"))} />
              <UsageCard compact label={language.t("cowork.usage.documents")} value={formatNumber(getNumber(stats()?.file_stats, "documents"))} />
              <UsageCard compact label={language.t("cowork.usage.previewableFiles")} value={formatNumber(getNumber(stats()?.file_stats, "previewable"))} />
              <UsageCard compact label={language.t("cowork.usage.filesWithoutPreview")} value={formatNumber(getNumber(stats()?.file_stats, "without_preview"))} />
            </div>
          </UsageSection>
          </div>
        <UsageSection title={language.t("cowork.usage.byProjectTitle")}>
          <UsageTable
            empty={language.t("cowork.usage.emptyTable")}
            rows={stats()?.by_project ?? []}
            columns={[
              { label: language.t("cowork.usage.project"), value: (row) => getString(row, "name") },
              { label: language.t("cowork.usage.chats"), value: (row) => formatNumber(getNumber(row, "chats")) },
              { label: language.t("cowork.usage.files"), value: (row) => formatNumber(getNumber(row, "files")) },
              { label: language.t("cowork.usage.totalTokens"), value: (row) => formatNumber(getNumber(row, "total_tokens")) },
              { label: language.t("cowork.usage.estimatedCost"), value: (row) => `$${getNumber(row, "estimated_cost").toFixed(4)}` },
            ]}
          />
        </UsageSection>
        <UsageSection title={language.t("cowork.usage.byWorkflowTitle")}>
          <UsageTable
            empty={language.t("cowork.usage.emptyTable")}
            rows={stats()?.by_workflow ?? []}
            columns={[
              { label: language.t("cowork.usage.workflow"), value: (row) => getString(row, "name") },
              { label: language.t("cowork.usage.chats"), value: (row) => formatNumber(getNumber(row, "chats")) },
              { label: language.t("cowork.usage.files"), value: (row) => formatNumber(getNumber(row, "files")) },
              { label: language.t("cowork.usage.totalTokens"), value: (row) => formatNumber(getNumber(row, "total_tokens")) },
              { label: language.t("cowork.usage.estimatedCost"), value: (row) => `$${getNumber(row, "estimated_cost").toFixed(4)}` },
            ]}
          />
        </UsageSection>
        <UsageSection title={language.t("cowork.usage.recentRunsTitle")}>
          <UsageTable
            empty={language.t("cowork.usage.emptyTable")}
            rows={stats()?.recent_runs ?? []}
            columns={[
              { label: language.t("cowork.usage.time"), value: (row) => formatDate(getString(row, "created_at")) },
              { label: language.t("cowork.usage.prompt"), value: (row) => truncate(getString(row, "prompt"), 58) },
              { label: language.t("cowork.usage.status"), value: (row) => getString(row, "status") },
              { label: language.t("cowork.usage.totalTokens"), value: (row) => formatNumber(getNumber(row, "total_tokens")) },
              { label: language.t("cowork.usage.duration"), value: (row) => durationCell(row) },
            ]}
          />
        </UsageSection>
        </div>
      </DialogBody>
    </DialogV2>
  )
}

function UsageCard(props: { label: string; value: string; compact?: boolean }) {
  return (
    <div class="rounded-xl border border-v2-border-border-base bg-v2-background-bg-base p-4 shadow-[var(--v2-elevation-card)]">
      <div class={props.compact ? "text-18-semibold text-v2-text-text-base" : "text-24-semibold text-v2-text-text-base"}>{props.value}</div>
      <div class="mt-1 text-12-regular text-v2-text-text-muted">{props.label}</div>
    </div>
  )
}

function UsageSection(props: { title: string; children: JSX.Element }) {
  return (
    <section class="rounded-2xl border border-v2-border-border-base bg-v2-background-bg-layer-01 p-4 shadow-[var(--v2-elevation-card)]">
      <h3 class="mb-3 border-b border-v2-border-border-base pb-3 text-14-medium text-v2-text-text-base">{props.title}</h3>
      {props.children}
    </section>
  )
}

function UsageTable(props: { rows: Record<string, unknown>[]; empty: string; columns: Array<{ label: string; value: (row: Record<string, unknown>) => string }> }) {
  return (
    <Show when={props.rows.length > 0} fallback={<p class="text-13-regular text-v2-text-text-muted">{props.empty}</p>}>
      <div class="overflow-x-auto">
        <table class="w-full min-w-[640px] text-left text-12-regular">
          <thead class="bg-v2-background-bg-layer-02 text-v2-text-text-muted">
            <tr>
              <For each={props.columns}>{(column) => <th class="border-b border-v2-border-border-base px-3 py-2 font-normal first:rounded-l-lg last:rounded-r-lg">{column.label}</th>}</For>
            </tr>
          </thead>
          <tbody>
            <For each={props.rows}>
              {(row) => (
                <tr class="text-v2-text-text-base">
                  <For each={props.columns}>{(column) => <td class="border-b border-v2-border-border-base/60 px-3 py-2.5 align-top">{column.value(row)}</td>}</For>
                </tr>
              )}
            </For>
          </tbody>
        </table>
      </div>
    </Show>
  )
}

function formatNumber(value: number) {
  return new Intl.NumberFormat().format(value)
}

function getNumber(row: Record<string, unknown> | undefined, key: string) {
  const value = row?.[key]
  return typeof value === "number" ? value : 0
}

function getString(row: Record<string, unknown>, key: string) {
  const value = row[key]
  return typeof value === "string" ? value : ""
}

function formatDate(value?: string) {
  if (!value) return ""
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ""
  return dateFormatter(undefined, { dateStyle: "medium", timeStyle: "short" }).format(date)
}

function truncate(value: string, length: number) {
  return value.length > length ? `${value.slice(0, length - 1)}…` : value
}

function durationCell(row: Record<string, unknown>) {
  const value = row.duration_seconds
  return typeof value === "number" ? `${formatNumber(value)}s` : ""
}

function formatDuration(minutes: number) {
  const hours = Math.floor(minutes / 60)
  const remaining = minutes % 60
  if (!hours) return `${formatNumber(remaining)}m`
  if (!remaining) return `${formatNumber(hours)}h`
  return `${formatNumber(hours)}h ${formatNumber(remaining)}m`
}
